import React, { FC, useEffect, useRef, useState } from 'react';
import translate from '@translate';
import { checkFormValidity } from 'src/utils/formFunctions';
import { OrderPalletType } from 'src/types/order';
import TextField from 'src/components/TextField';
import Grid, { GridItem } from 'src/components/Grid';
import Button from 'src/components/Button';
import Typography from 'src/components/Typography';
import { colors, sizes } from 'src/themes/default';
import { PalletEditKeysType } from './constants';
import { FormErrorsType } from '../../types/global';
import Select from '../../components/Select';
import { useDispatch, useSelector } from 'react-redux';
import { RootStoreType } from '../../types/store';
import { getPalletTypesFetch } from '../../resources/orderDetail/reducer';

const initData: OrderPalletType = {};
type NewPalletBlockPropsType = {
    data?: OrderPalletType[];
    onChange: (value: any) => void;
    disableEdit?: boolean;
    editErrors: FormErrorsType;
    idOrder?: number;
};

const NewPalletBlock: FC<NewPalletBlockPropsType> = (props) => {
    const formRef = useRef<HTMLFormElement>(null);
    const dispatch = useDispatch();
    const [editData, setEditData] = useState<OrderPalletType>(initData);
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const palletTypes = useSelector((state: RootStoreType) => state.orderDetail.palletTypes);

    const changeValue = (key: PalletEditKeysType, value: any, error: boolean) => {
        const newData = { ...editData };
        newData[key] = value;
        const newErrors = { ...editErrors };
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    useEffect(() => {
        if (!palletTypes) {
            dispatch(getPalletTypesFetch(props.idOrder));
        }
    }, [props.idOrder]);

    const handleAddRow = () => {
        const newFormErrors = checkFormValidity(formRef);

        if (!Object.keys(newFormErrors).length) {
            // Generate a unique negative ID for new pallets
            const newId = Math.min.apply(0, [0, ...(props.data || []).map((item: any): any => item.ID || 0)]) - 1;

            // Create a new pallet with the form data
            const newPallet = {
                ...editData,
                ID: newId
            };

            // Add the new pallet to the existing data
            const updatedPallets = [...(props.data || []), newPallet];

            // Update the parent component with the new data
            props.onChange(updatedPallets);

            // Reset the form
            setEditData(initData);
        }

        setEditErrors(newFormErrors);
    };

    return (
        <>
            <Typography gutterBottom variant="h3" sx={props.editErrors.ORDER_PALLETS ? { color: colors.alert_O } : {}}>
                {translate('orderDetail.addPallet')}
            </Typography>
            <form ref={formRef}>
                <Grid>
                    <GridItem cols={3}>
                        <Select
                            value={editData.PALLET_TYPE}
                            options={palletTypes?.map((item) => ({
                                label: item.NAME,
                                value: item.NAME
                            })) || []}
                            error={editErrors.PALLET_TYPE}
                            onChange={(value, error) => changeValue('PALLET_TYPE', value, error)}
                            name="PALLET_TYPE_ID"
                            label={translate('orderDetail.palletTable.palletType')}
                            fullWidth
                            disabled={props.disableEdit}
                            required
                        />
                    </GridItem>

                    <GridItem cols={1}>
                        <TextField
                            value={editData.WEIGHT}
                            error={editErrors.WEIGHT}
                            onChange={(value, error) => changeValue('WEIGHT', value, error)}
                            name="WEIGHT"
                            label={translate('orderDetail.palletTable.weight')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                            step="0.01"
                            disabled={props.disableEdit}
                            required
                        />
                    </GridItem>
                    <GridItem cols={1}>
                        <TextField
                            value={editData.LENGTH}
                            error={editErrors.LENGTH}
                            onChange={(value, error) => changeValue('LENGTH', value, error)}
                            name="LENGTH"
                            label={translate('orderDetail.palletTable.length')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                            step="1"
                            disabled={props.disableEdit}
                            required
                        />
                    </GridItem>
                    <GridItem cols={1}>
                        <TextField
                            value={editData.WIDTH}
                            error={editErrors.WIDTH}
                            onChange={(value, error) => changeValue('WIDTH', value, error)}
                            name="WIDTH"
                            label={translate('orderDetail.palletTable.width')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                            step="1"
                            disabled={props.disableEdit}
                            required
                        />
                    </GridItem>
                    <GridItem cols={1}>
                        <TextField
                            value={editData.HEIGHT}
                            error={editErrors.HEIGHT}
                            onChange={(value, error) => changeValue('HEIGHT', value, error)}
                            name="HEIGHT"
                            label={translate('orderDetail.palletTable.height')}
                            fullWidth
                            type="numberLocale"
                            min={0}
                            step="1"
                            disabled={props.disableEdit}
                            required
                        />
                    </GridItem>
                    <GridItem cols={2}>
                        <Button
                            onClick={handleAddRow}
                            disabled={props.disableEdit}
                            sx={{ marginTop: sizes.input_top_PD, fontWeight: 'normal' }}
                            mobileFullWidht
                        >
                            + {translate('orderDetail.palletTable.action.add')}
                        </Button>
                    </GridItem>
                </Grid>
            </form>
        </>
    );
};

export default NewPalletBlock;
